import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuidv4 } from 'uuid';

import {
  ENVIRONMENT,
  APP_VERSION,
} from 'src/app/shared/constants/configObjectProperties';
import { ALERT_VIEWED } from 'src/app/shared/dataAnalytics/schema/constants';
import { getConfig } from 'src/app/utils/config';
import { isStaging, isProd } from 'src/app/utils/environment';
import { actionTrackError } from 'src/app/v1/state/modules/tealium/trackError';
import { store } from 'src/app/v1/state/store';
import type { TrackingEvent } from 'src/types/trackingData';

export const log = {
  error: (message: string, meta?: any, shouldFireTealium?: boolean) => {
    datadogLogger.error(message, meta);

    if (shouldFireTealium) {
      const payload: TrackingEvent = {
        eventName: ALERT_VIEWED,
        errorMessage: message,
        data: meta,
        isTrackedError: true,
      };
      store.dispatch(actionTrackError(payload));
    }

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.error(message, meta);
    }
  },
  info: (...message: unknown[]) => {
    datadogLogger.info(...message);

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.info(...message);
    }
  },
  warn: (...message: unknown[]) => {
    datadogLogger.warn(...message);

    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(...message);
    }
  },
  trace: (...message: unknown[]) => {
    // For debugging/tracing events that we don't want to go to Datadog
    if (getConfig(ENVIRONMENT) !== 'production') {
      // eslint-disable-next-line no-console
      console.debug(...message);
    }
  },
};

export const loadDatadog = () => {
  if (isProd() || isStaging()) {
    datadogRum.init({
      applicationId: '8a39bca4-cf74-4685-90a7-1ab07a9275a2',
      clientToken: 'pubdccddf693dd56ce37f3bc20b4a22021d',
      env: getConfig(ENVIRONMENT),
      site: 'datadoghq.com',
      service: 'express-ui',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      version: getConfig(APP_VERSION),
      enableExperimentalFeatures: ['feature_flags'],
      allowedTracingUrls: [
        /https:\/\/talent\.seek\.com\.au(?!\/graphql).*/,
        /https:\/\/talent\.seek\.co\.nz(?!\/graphql).*/,
        /https:\/\/talent\.staging\.seek\.com\.au(?!\/graphql).*/,
        /https:\/\/talent\.staging\.seek\.co\.nz(?!\/graphql).*/,
        // Asia Domains
        /https:\/\/ph\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/ph\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/th\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/th\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/my\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/my\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/hk\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/hk\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/id\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/id\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /https:\/\/sg\.employer\.seek\.com(?!\/graphql).*/,
        /https:\/\/sg\.employer\.staging\.seek\.com(?!\/graphql).*/,
        /http:\/\/hirer-graphql-api-.*\.hirer\.dev\.outfra\.xyz/,
      ],
      defaultPrivacyLevel: 'mask-user-input',
    });

    datadogLogs.init({
      clientToken: 'pub8fe228b74adad734c70245f18a18eddd',
      site: 'datadoghq.com',
      service: 'express-ui',
      env: getConfig(ENVIRONMENT),
      version: getConfig(APP_VERSION),
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });

    const appUuid = uuidv4();
    addDatadogGlobalContext('appUuid', appUuid);

    datadogRum.startSessionReplayRecording();
  }
};

const addDatadogGlobalContext = (
  key: string,
  value: string | Record<string, unknown>,
) => {
  datadogRum.setGlobalContextProperty(key, value);
};

const setDatadogUser = (seekAdvertiserId: string) => {
  datadogRum.setUser({ id: seekAdvertiserId });
  addDatadogGlobalContext('SeekAdvertiserId', seekAdvertiserId);
};

export const setUser = (seekAdvertiserId: string) => {
  setDatadogUser(seekAdvertiserId);
};

export const setExperiment = (testingId: string) => {
  addDatadogGlobalContext('Experiment', testingId);
};

interface DataDogLogContext extends Record<string, unknown> {
  message?: string;
  activity?: string;
}

const mapLogInput: (
  message?: unknown,
  context?: unknown,
) => { message: string; context?: DataDogLogContext } = (message, context) => ({
  message: (message as string | undefined) ?? 'log',
  context: context as DataDogLogContext,
});

const datadogLogger = {
  info: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);
    datadogLogs.logger.info(message, context);
    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      message,
      level: 'info',
    });
  },
  error: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);
    const error = (context?.error as Error) ?? new Error(message);
    datadogRum.addError(error, {
      ...context,
      message,
      level: 'error',
    });
    datadogLogs.logger.error(message, context, error);
  },
  warn: (messageInput?: unknown, contextInput?: unknown) => {
    const { message, context } = mapLogInput(messageInput, contextInput);
    datadogLogs.logger.warn(message, context);
    datadogRum.addAction(context?.activity ?? message, {
      ...context,
      message,
      level: 'warn',
    });
  },
};
